import { appPath } from "../appPath";
import { useEffect, useRef, useState } from "react";

import {
  clearPlatformToken,
  getPlatformIdentity,
  logoutPlatformAdmin,
  type PlatformIdentity,
  type PlatformPermission,
} from "./api";
import { InstitutionAccounts } from "./InstitutionAccounts";
import { LawyerIdentityReviews } from "./LawyerIdentityReviews";
import { PersonalLawyers } from "./PersonalLawyers";
import { PaymentCollections } from "./PaymentCollections";
import { BillingReconciliation } from "./BillingReconciliation";
import { AgentReconciliation } from "./AgentReconciliation";
import { ExceptionOrdersWorkspace } from "./ExceptionOrdersWorkspace";
import { PlatformSubaccounts } from "./PlatformSubaccounts";
import { PropertyOrders } from "./PropertyOrders";
import { SupportTickets } from "./SupportTickets";

export type PlatformSection = "property-orders" | "exception-orders" | "support" | "institutions" | "personal-lawyers" | "lawyer-identity-reviews" | "payment-collections" | "platform-subaccounts" | "billing-reconciliation" | "agent-reconciliation";

export type PlatformSectionDescriptor = {
  id: PlatformSection;
  label: string;
  permission?: PlatformPermission;
  ownerOnly?: boolean;
};

const PLATFORM_SECTIONS: PlatformSectionDescriptor[] = [
  { id: "property-orders", label: "不动产订单", permission: "PROPERTY_ORDERS" },
  { id: "exception-orders", label: "异常订单", permission: "EXCEPTION_ORDERS" },
  { id: "support", label: "客服咨询", permission: "SUPPORT" },
  { id: "institutions", label: "机构账户", ownerOnly: true },
  { id: "personal-lawyers", label: "个人律师", permission: "PERSONAL_LAWYERS" },
  { id: "lawyer-identity-reviews", label: "实名认证审核", permission: "LAWYER_IDENTITY_REVIEWS" },
  { id: "payment-collections", label: "收款明细", permission: "PAYMENT_COLLECTIONS" },
  { id: "billing-reconciliation", label: "计费对账", ownerOnly: true },
  { id: "agent-reconciliation", label: "代理对账" },
  { id: "platform-subaccounts", label: "平台子账户", ownerOnly: true },
];

export function visiblePlatformSections(identity: PlatformIdentity): PlatformSectionDescriptor[] {
  if (identity.kind === "OWNER") return PLATFORM_SECTIONS;
  if (identity.is_agent) return PLATFORM_SECTIONS.filter((item) => item.id === "agent-reconciliation" || (item.permission && identity.permissions.includes(item.permission)));
  return PLATFORM_SECTIONS.filter(
    (item) => item.permission && identity.permissions.includes(item.permission),
  );
}

export function activePlatformSection(
  identity: PlatformIdentity,
  requested: PlatformSection,
): PlatformSection | null {
  const sections = visiblePlatformSections(identity);
  return sections.some((item) => item.id === requested) ? requested : sections[0]?.id ?? null;
}

export function PlatformAdminShell() {
  const [identity, setIdentity] = useState<PlatformIdentity | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [section, setSection] = useState<PlatformSection>("property-orders");
  const accountMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void getPlatformIdentity().then(setIdentity).catch(() => {
      clearPlatformToken();
      window.location.assign(appPath("/?login=1"));
    });
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) return;

    function closeOnOutsideClick(event: MouseEvent) {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setAccountMenuOpen(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [accountMenuOpen]);

  function logout() {
    void logoutPlatformAdmin().finally(() => window.location.assign(appPath("/")));
  }

  const visibleSections = identity ? visiblePlatformSections(identity) : [];
  const visibleSectionIds = new Set(visibleSections.map((item) => item.id));
  const activeSection = identity ? activePlatformSection(identity, section) : null;

  return (
    <main className="platform-admin-shell">
      <header className="platform-admin-header">
        <span className="platform-admin-brand">中承信合数据服务平台 · 平台管理端</span>
        <div className="platform-admin-account" ref={accountMenuRef}>
          <button
            className="platform-admin-account-trigger"
            type="button"
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            onClick={() => setAccountMenuOpen((open) => !open)}
          >
            <span className="platform-admin-avatar" aria-hidden="true">
              {(identity?.name ?? "平台管理员").slice(0, 1)}
            </span>
            <span className="platform-admin-account-name">{identity?.name ?? "平台管理员"}</span>
            <span className="platform-admin-chevron" aria-hidden="true">
              <svg viewBox="0 0 16 16" focusable="false">
                <path d="M4 3.75 8 7.5l4-3.75" />
                <path d="M4 8.25 8 12l4-3.75" />
              </svg>
            </span>
          </button>
          {accountMenuOpen && (
            <div className="platform-admin-account-menu" role="menu">
              <div className="platform-admin-account-summary">
                <strong>{identity?.name ?? "平台管理员"}</strong>
                <span>{identity?.kind === "SUBACCOUNT" ? "平台子账户" : "平台管理员"}</span>
              </div>
              <button type="button" role="menuitem" onClick={logout}>退出登录</button>
            </div>
          )}
        </div>
      </header>
      {identity && visibleSections.length > 0 && (
        <nav className="platform-admin-nav" aria-label="平台管理模块">
          {visibleSectionIds.has("property-orders") && <button
            className={activeSection === "property-orders" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("property-orders")}
          >不动产订单</button>}
          {visibleSectionIds.has("exception-orders") && <button
            className={activeSection === "exception-orders" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("exception-orders")}
          >异常订单</button>}
          {visibleSectionIds.has("support") && <button
            className={activeSection === "support" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("support")}
          >客服咨询</button>}
          {identity?.kind === "OWNER" && <button
            className={activeSection === "institutions" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("institutions")}
          >机构账户</button>}
          {visibleSectionIds.has("personal-lawyers") && <button
            className={activeSection === "personal-lawyers" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("personal-lawyers")}
          >个人律师</button>}
          {visibleSectionIds.has("lawyer-identity-reviews") && <button
            className={activeSection === "lawyer-identity-reviews" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("lawyer-identity-reviews")}
          >实名认证审核</button>}
          {visibleSectionIds.has("payment-collections") && <button
            className={activeSection === "payment-collections" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("payment-collections")}
          >收款明细</button>}
          {identity?.kind === "OWNER" && <button
            className={activeSection === "billing-reconciliation" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("billing-reconciliation")}
          >计费对账</button>}
          {visibleSectionIds.has("agent-reconciliation") && <button className={activeSection === "agent-reconciliation" ? "is-active" : ""} type="button" onClick={() => setSection("agent-reconciliation")}>代理对账</button>}
          {identity?.kind === "OWNER" && <button
            className={activeSection === "platform-subaccounts" ? "is-active" : ""}
            type="button"
            onClick={() => setSection("platform-subaccounts")}
          >平台子账户</button>}
        </nav>
      )}
      {identity === null && <section className="platform-admin-empty"><p>正在加载平台管理端…</p></section>}
      {identity && activeSection === "property-orders" && <PropertyOrders />}
      {identity && activeSection === "exception-orders" && <ExceptionOrdersWorkspace />}
      {identity && activeSection === "support" && <SupportTickets />}
      {identity?.kind === "OWNER" && activeSection === "institutions" && <InstitutionAccounts />}
      {identity && activeSection === "personal-lawyers" && <PersonalLawyers />}
      {identity && activeSection === "lawyer-identity-reviews" && <LawyerIdentityReviews />}
      {identity && activeSection === "payment-collections" && <PaymentCollections />}
      {identity?.kind === "OWNER" && activeSection === "billing-reconciliation" && <BillingReconciliation />}
      {identity && activeSection === "agent-reconciliation" && <AgentReconciliation isOwner={identity.kind === "OWNER"} />}
      {identity?.kind === "OWNER" && activeSection === "platform-subaccounts" && <PlatformSubaccounts />}
      {identity && visibleSections.length === 0 && (
        <section className="platform-admin-empty"><p>暂未开通平台管理权限</p></section>
      )}
    </main>
  );
}
